import { Link } from 'react-router-dom';

import { Permission } from '../../../../../../../../../../backend/src/auth/permissions/permission';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import useUpdateDoc from '@/api/mutations/useUpdateDoc';
import { Doc } from '@/api/types/backend-dto-types';
import { useHasPermission } from '@/hooks/useHasPermission';
import { EditFieldDialog } from '@/new/components/edit-field-dialog';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Tabs, TabsList, TabsTrigger } from '@/new/components/shadcn/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';

type PanelMode = 'rawDoc' | 'details';

interface DocPanelHeaderProps {
  doc: Doc;
  matterId: string;
  subsetId: string;
  panelMode: PanelMode;
  setPanelMode: (mode: PanelMode) => void;
}

export function DocPanelHeader({ doc, matterId, subsetId, panelMode, setPanelMode }: DocPanelHeaderProps) {
  const { hasPermission } = useHasPermission();
  const canUpdateDoc = hasPermission(Permission.DocumentUpdate);

  const { mutate: updateDocFileName } = useUpdateDoc();

  return (
    <div className="flex flex-col border-b bg-gray-50 p-3">
      <div className="flex items-center justify-between gap-4">
        <Tabs value={panelMode}>
          <TabsList variant="pill">
            <TabsTrigger value="rawDoc" onMouseDown={(e) => e.preventDefault()} onClick={() => setPanelMode('rawDoc')}>
              <Icon className="lucide--file-text" />
              Document
            </TabsTrigger>
            <TabsTrigger
              value="details"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setPanelMode('details')}
            >
              <Icon className="lucide--info" />
              Details
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button asChild size="sm" variant="white" className="rounded-full">
              <Link to={`/matters/${matterId}/subsets/${subsetId}/docs/${doc.id}`} aria-label="View document facts">
                View Full Doc
                <Icon className="lucide--arrow-right size-3" />
              </Link>
            </Button>
          </TooltipTrigger>
          <TooltipContent>View document alongside its facts</TooltipContent>
        </Tooltip>
      </div>

      <div className="px-2 pt-3">
        <div className="text-sm font-medium break-words text-gray-700">
          {doc.file.name}
          {canUpdateDoc && (
            <EditFieldDialog
              title="Edit document name"
              description="Update the file name for this document."
              fieldLabel="Document name"
              currentValue={doc.file.name}
              onSave={(value) => {
                const trimmed = value.trim();
                if (!trimmed || trimmed === doc.file.name) return;
                updateDocFileName({ matterId, docId: doc.id, field: 'filename', value: trimmed });
                trackCustomEvent(TrackingEventsV2.DocumentUpdateName, { documentId: doc.id });
              }}
              triggerClassName="ml-1.5 inline-flex h-5 w-5 -translate-y-px items-center justify-center align-middle px-0 py-0"
            />
          )}
        </div>
      </div>
    </div>
  );
}
